// ============================================================
// Commission — access control + the math
// ============================================================

// ── Who can see commission ──────────────────────────────────────────────────
// These must match the UUIDs in commission_setup.sql. The UI check hides the
// nav item; the RLS policies in the database are what actually protect the data.
export const COMMISSION_VIEWERS = [
  '5dff583e-c52d-4954-b09f-a3334f7590d1',  // Lauren Parker
  'a4a45550-b175-422b-ab0b-9d5d4c0e69b1',  // Tori Gilmore
]

// Only these people can edit amounts, notes, retainer changes, bonuses and
// paid dates. Tori can view everything and can record commission when she
// adds a client during onboarding.
export const COMMISSION_EDITORS = [
  '5dff583e-c52d-4954-b09f-a3334f7590d1',  // Lauren Parker
]

export const canViewCommission = (userId) => COMMISSION_VIEWERS.includes(userId)
export const canEditCommission = (userId) => COMMISSION_EDITORS.includes(userId)

// ── The rules ───────────────────────────────────────────────────────────────
export const COMMISSION_RATE = 0.05   // 5% of the monthly retainer
export const COMMISSION_MONTHS = 6    // paid for the first 6 months
// Month 1 is the month of the client's start date.
// No commission for the month a client terminates, or any month after.
// A downgrade changes the amount from its effective month on; it doesn't
// restart or extend the 6-month window.

// ── Month helpers ("YYYY-MM" keys, no time zones involved) ──────────────────
export const monthKey = (dateStr) => (dateStr ? String(dateStr).slice(0, 7) : null)

export const addMonths = (key, n) => {
  const [y, m] = key.split('-').map(Number)
  const idx = y * 12 + (m - 1) + n
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`
}

export const monthStart = (key) => `${key}-01`   // for date columns in the DB

export const currentMonthKey = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const monthLabel = (key, withYear = true) => {
  const [y, m] = key.split('-').map(Number)
  return withYear ? `${MONTH_NAMES[m - 1]} ${y}` : MONTH_NAMES[m - 1]
}

export const commissionFor = (retainer) => Math.round(Number(retainer || 0) * COMMISSION_RATE * 100) / 100

export const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// Local calendar date of a timestamp, as YYYY-MM-DD.
const localDate = (iso) => {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// ── Termination ─────────────────────────────────────────────────────────────
// A date entered on the deal itself wins (used for history imported from the
// spreadsheet and for deals not linked to a client). Otherwise the client's
// termination date, falling back to the day they were archived.
export const terminationDate = (deal, client) => {
  if (deal.terminated_on) return deal.terminated_on
  if (!client) return null
  if (client.terminated_on) return client.terminated_on
  if (client.archived_at) return localDate(client.archived_at)
  return null
}

export const dealWindow = (deal) => {
  const first = monthKey(deal.start_date)
  return Array.from({ length: COMMISSION_MONTHS }, (_, i) => addMonths(first, i))
}

// Retainer in effect for a month: the latest change effective on or before it.
export const retainerFor = (deal, changes = [], key) => {
  let best = null
  changes.forEach(c => {
    const k = monthKey(c.effective_month)
    if (k <= key && (!best || k > monthKey(best.effective_month))) best = c
  })
  return Number(best ? best.monthly_retainer : deal.monthly_retainer)
}

// Everything the grid needs for one deal in one month.
//   state: 'outside' | 'active' | 'terminated'
//   amount: what's owed (override wins when set)
export const cellFor = (deal, key, { changes = [], entry = null, client = null } = {}) => {
  const months = dealWindow(deal)
  const monthNumber = months.indexOf(key) + 1         // 1–6, or 0 when outside
  const term = terminationDate(deal, client)
  const termKey = monthKey(term)

  let state = 'outside'
  if (monthNumber > 0) state = termKey && key >= termKey ? 'terminated' : 'active'

  const ruleAmount = state === 'active' ? commissionFor(retainerFor(deal, changes, key)) : 0
  const hasOverride = entry && entry.amount_override !== null && entry.amount_override !== undefined
  const amount = hasOverride ? Number(entry.amount_override) : ruleAmount

  return { key, state, monthNumber, ruleAmount, amount, hasOverride, note: entry?.note || '', termKey }
}

// Status of the whole deal as of a given month (defaults to today).
export const dealStatus = (deal, client, asOf = currentMonthKey()) => {
  const months = dealWindow(deal)
  const last = months[months.length - 1]
  const termKey = monthKey(terminationDate(deal, client))
  if (termKey && termKey <= last && termKey <= asOf) return 'terminated'
  if (asOf < months[0]) return 'upcoming'
  if (asOf > last) return 'complete'
  return 'active'
}

// Does this deal have anything to show in the given year?
export const dealTouchesYear = (deal, year, entries = []) => {
  const y = String(year)
  return dealWindow(deal).some(k => k.startsWith(y)) ||
    entries.some(e => e.deal_id === deal.id && String(e.month).startsWith(y))
}
